import React from 'react'
// components
import Image from 'next/image'
import { ButtonEdit } from '@/components/commons/buttons/ButtonEdit.component'
import { TextArea } from '@/components/commons/forms/TextArea.component'
import { InputLocationComponent } from '@/components/commons/forms/input/InputLocation.component'
import { AnimateContainer } from '@/components/commons/container'

import { IMAGE_PATHS_V2 } from '@/shared/consts/imagesPaths'
import { ProfilePageContext } from '../../../../context/profilePage/ProfilePageContext'

// styles
import { section } from 'styles/v2/pages/profile'
import { ButtonComponent } from '@/components/commons/buttons'
import { UserContext } from 'context'
import { cssClass } from '@/shared/utils'
import { Text } from '@/components/commons/typography/Text'
import { helperTextStyle } from './styles/profilePersonalInfo.css'

export const ProfileAbout: React.FC = () => {
  const { profile, ownProfile } = React.useContext(ProfilePageContext)
  const { updateUser } = React.useContext(UserContext)
  const [isEdditing, setIsEditing] = React.useState(false)
  const [provisionalAbout, setProvisionalAbout] = React.useState(
    profile?.about || ''
  )
  const [provisionalLocation, setProvisionalLocation] = React.useState(
    profile?.location || ''
  )
  const [loading, setLoading] = React.useState(false)
  const [deactiveAnimation, setDeactiveAnimation] = React.useState(false)
  const editable = ownProfile

  const showSaveButton =
    provisionalAbout !== profile?.about ||
    provisionalLocation !== profile?.location

  const maxAboutCharacters = 150

  const remainingCharacters = maxAboutCharacters - provisionalAbout.length

  const onCancel = () => {
    setIsEditing(false)
    setProvisionalAbout(profile?.about || '')
    setProvisionalLocation(profile?.location || '')
  }

  const onSave = async () => {
    setLoading(true)
    await updateUser({
      about: provisionalAbout.slice(0, 150),
      location: provisionalLocation,
    })
    setIsEditing(false)
    setLoading(false)
  }

  React.useEffect(() => {
    setProvisionalAbout(profile?.about || '')
    setProvisionalLocation(profile?.location || '')
  }, [profile?.about, profile?.location])

  React.useEffect(() => {
    if (isEdditing) {
      setDeactiveAnimation(true)
    }
  }, [isEdditing])

  return (
    <div className={cssClass([section, 'mt-20'])}>
      <div className="flex justify-space-between items-center">
        <h3 className="text-16 text-primary m-0 mb-12">About</h3>
        {editable && <ButtonEdit onClick={() => setIsEditing((e) => !e)} />}
      </div>
      {isEdditing && ownProfile ? (
        <React.Fragment>
          <div className={cssClass([helperTextStyle, 'mb-12 text-left'])}>
            {remainingCharacters} characters remaining
          </div>
          <TextArea
            value={provisionalAbout}
            onChange={(e) => setProvisionalAbout(e.target.value)}
            placeholder="Let the people know more about you!"
          />
        </React.Fragment>
      ) : (
        profile?.about && (
          <AnimateContainer
            type="fadeInLeft"
            setOpacityNone={!deactiveAnimation}
            duration={0.4}
            manualActive={true}
            active={!deactiveAnimation}
          >
            <Text color="text-secondary" sizeClass="text-14">
              {profile?.about}
            </Text>
          </AnimateContainer>
        )
      )}
      {isEdditing && ownProfile ? (
        <InputLocationComponent
          value={provisionalLocation}
          containerClassName={['m-none']}
          onChange={(e) => setProvisionalLocation(e.target.value)}
          placeholder="Where are you located?"
        />
      ) : (
        location && (
          <AnimateContainer
            delay={200}
            type="fadeInLeft"
            setOpacityNone={!deactiveAnimation}
            duration={0.4}
            manualActive={true}
            active={!deactiveAnimation}
          >
            <div className="flex items-center gap-6 mt-12">
              <div>
                <Image
                  src={IMAGE_PATHS_V2.icons.commons + 'location.svg'}
                  alt="location"
                  width={18}
                  height={18}
                />
              </div>
              <Text color="text-secondary" sizeClass="text-14">
                {profile?.location}
              </Text>
            </div>
          </AnimateContainer>
        )
      )}
      {isEdditing && showSaveButton && ownProfile && (
        <div className="flex mt-12 gap-2">
          <ButtonComponent
            variation="secondary"
            label="Cancel"
            fullWidth
            onClick={onCancel}
            loading={loading}
            size="xSmall"
          />
          <ButtonComponent
            variation="primary"
            label="Save"
            size="xSmall"
            fullWidth
            onClick={onSave}
            loading={loading}
          />
        </div>
      )}
    </div>
  )
}
